




































import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { SubscriptionsSearchResult } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";

export default defineComponent({
  name: "BlockUserController",
  components: { BaseConfirm, BaseButton },
  props: {
    opponent: { type: Object as () => UserPublicData, required: true },
    showConfirm: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser, isCreator } = useAuthUser();

    const blockingStatus = ref<BlockingStatus[]>([]);
    const isOpponentSubscriber = ref(false);
    const loading = ref(false);

    const opponentBlocked = computed(() =>
      blockingStatus.value.some(s => s.userId === authUser.value?.id && s.blockedUserId === props.opponent.id)
    );
    const authUserBlocked = computed(() =>
      blockingStatus.value.some(s => s.userId === props.opponent.id && s.blockedUserId === authUser.value?.id)
    );
    const publicBlockingStatus = computed(() => ({
      authUser: authUserBlocked.value,
      opponent: opponentBlocked.value,
    }));
    const acceptButtonText = computed(() => $t(opponentBlocked.value ? "Unblock" : "Block"));
    const blockingConfirmText = computed(() =>
      opponentBlocked.value
        ? $t("Unblock {0}", [props.opponent?.username])
        : $t("Block {0}", [props.opponent?.username])
    );

    const toggleBlockingStatus = async () => {
      try {
        loading.value = true;

        if (opponentBlocked.value) {
          await providers.BlockingStatusProvider.unblock(props.opponent.id);
        } else {
          await providers.BlockingStatusProvider.block(props.opponent.id);
        }
      } finally {
        loading.value = false;
        emit("close");
      }
    };

    const emitBlockingStatusUp = () => emit("blockingStatusChange", publicBlockingStatus.value);

    const setBlockingStatusToBuffer = (authUserId: string, opponentId: string, time: string) => {
      const blockingUserIndex = blockingStatus.value.findIndex(s => s.userId === authUserId);

      if (blockingUserIndex === -1) {
        blockingStatus.value.push({
          userId: authUserId,
          blockedUserId: opponentId,
          time: time,
        });
      } else {
        blockingStatus.value.splice(blockingUserIndex, 1);
      }
    };

    const accept = async () => {
      if (authUser.value) {
        await toggleBlockingStatus();
        setBlockingStatusToBuffer(authUser.value.id, props.opponent.id, new Date().toISOString());
        emitBlockingStatusUp();
      }
    };

    const blockingStatusChangeHandler = (notification?: Notification): void => {
      if (!notification) return;
      const blockedUserId = notification.recipientId;
      const blockingUserId = notification.subjectId;

      if (blockingUserId !== props.opponent.id) {
        return;
      }

      setBlockingStatusToBuffer(blockingUserId, blockedUserId, notification.time);
      emitBlockingStatusUp();
    };

    const getOpponentSubscription = async () => {
      if (!isCreator.value) return;
      const opponentSubscriptionResponse: SubscriptionsSearchResult = await providers.SubscriptionProvider.search(
        {
          data: {
            consumerIds: [props.opponent.id],
            providerId: authUser.value?.id ?? "",
            expired: false,
            size: 1,
          },
        },
        ["id"]
      );
      isOpponentSubscriber.value = !!opponentSubscriptionResponse.result.length;
    };

    const getBlockingStatus = async () => {
      if (authUser.value)
        blockingStatus.value = await providers.BlockingStatusProvider.searchByOpponents({
          data: { opponentIds: [props.opponent.id] },
        });
    };

    const getData = () => {
      Promise.all([getBlockingStatus().then(emitBlockingStatusUp), getOpponentSubscription()]);
    };

    getData();
    emitter.on<Notification>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler);
    onBeforeUnmount(() => emitter.off<Notification>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler));

    return {
      loading,
      opponentBlocked,
      acceptButtonText,
      blockingConfirmText,
      isOpponentSubscriber,
      accept,
    };
  },
});
